// 全局背景音乐管理器
class MusicManager {
  constructor() {
    this.audio = null;
    this.isPlaying = false;
    // 默认关闭背景音乐
    this.isEnabled = false;
    // 默认音量设置为30%（0.3）
    this.defaultVolume = 0.3;
    // 不在构造函数中自动初始化，需要登录后手动调用 init()
  }

  // 初始化音频（登录后调用）
  init() {
    // 检查是否已登录（有 token）
    const token = localStorage.getItem('token');
    if (!token) {
      // 未登录，不初始化音乐
      return;
    }

    if (!this.audio) {
      this.audio = new Audio(require("@/assets/bm.mp3"));
      this.audio.loop = true;
      // 设置默认音量（30%）
      this.audio.volume = this.defaultVolume;
      
      // 监听音频错误
      this.audio.addEventListener('error', (e) => {
        console.error('背景音乐加载失败:', e);
      });

      // 监听音频播放结束（虽然设置了loop，但以防万一）
      this.audio.addEventListener('ended', () => {
        if (this.isEnabled && this.isPlaying) {
          this.audio.play().catch(err => {
            console.error('背景音乐播放失败:', err);
          });
        }
      });

      // 监听页面可见性变化，确保页面激活时音乐能继续播放
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && this.isEnabled && !this.isPlaying) {
          // 页面变为可见且音乐应该播放时，恢复播放
          this.play();
        }
      });
    }

    // 从 localStorage 读取初始状态
    this.loadState();
    
    // 如果启用，则播放
    if (this.isEnabled) {
      this.play();
    }
  }

  // 从 localStorage 加载状态
  loadState() {
    try {
      const userInfoMsg = localStorage.getItem("userInfoMsg");
      if (userInfoMsg) {
        const userInfo = JSON.parse(userInfoMsg);
        // 如果 alphaMusic 字段存在且明确设置为 1，才开启；否则默认关闭
        if (userInfo.alphaMusic === 1) {
          this.isEnabled = true;
        } else {
          // alphaMusic 不存在、为 null、为 0 或其他值，都默认关闭
          this.isEnabled = false;
        }
      } else {
        // 如果 userInfoMsg 不存在，保持默认关闭状态
        this.isEnabled = false;
      }
    } catch (e) {
      console.error('读取音乐状态失败:', e);
      // 读取失败时保持默认关闭状态
      this.isEnabled = false;
    }
  }

  // 获取当前状态
  getState() {
    return this.isEnabled;
  }

  // 设置音乐开关状态
  setState(enabled) {
    this.isEnabled = enabled;
    
    // 更新 localStorage
    try {
      const userInfoMsg = localStorage.getItem("userInfoMsg");
      if (userInfoMsg) {
        const userInfo = JSON.parse(userInfoMsg);
        userInfo.alphaMusic = enabled ? 1 : 0;
        localStorage.setItem("userInfoMsg", JSON.stringify(userInfo));
      }
    } catch (e) {
      console.error('保存音乐状态失败:', e);
    }

    // 根据状态播放或暂停
    if (enabled) {
      this.play();
    } else {
      this.pause();
    }
  }

  // 播放音乐
  play() {
    if (!this.audio) {
      this.init();
      // 如果初始化后 audio 仍然为 null（未登录），则不播放
      if (!this.audio) {
        return;
      }
    }
    
    if (this.isEnabled && !this.isPlaying && this.audio) {
      // 使用 Promise 处理播放，如果失败则重试
      const tryPlay = () => {
        this.audio.play().then(() => {
          this.isPlaying = true;
        }).catch(err => {
          console.error('背景音乐播放失败:', err);
          this.isPlaying = false;
          // 如果是自动播放被阻止，等待用户交互后再试
          if (err.name === 'NotAllowedError') {
            // 监听一次用户交互后重试
            const retryOnInteraction = () => {
              if (this.isEnabled && !this.isPlaying) {
                this.audio.play().then(() => {
                  this.isPlaying = true;
                }).catch(() => {
                  this.isPlaying = false;
                });
              }
              document.removeEventListener('click', retryOnInteraction);
              document.removeEventListener('touchstart', retryOnInteraction);
            };
            document.addEventListener('click', retryOnInteraction, { once: true });
            document.addEventListener('touchstart', retryOnInteraction, { once: true });
          }
        });
      };
      
      // 如果音频还没加载完成，等待加载完成后再播放
      if (this.audio.readyState >= 2) {
        tryPlay();
      } else {
        this.audio.addEventListener('canplay', tryPlay, { once: true });
      }
    }
  }

  // 暂停音乐
  pause() {
    if (this.audio && this.isPlaying) {
      this.audio.pause();
      this.isPlaying = false;
    }
  }

  // 退出登录时清理音乐状态
  logout() {
    // 暂停音乐
    this.pause();
    // 关闭音乐开关（默认关闭）
    this.isEnabled = false;
    // 重置播放状态
    this.isPlaying = false;
    
    // 销毁音频资源，确保音乐完全停止
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0; // 重置播放位置
      this.audio = null; // 释放音频资源
    }
    
    // 更新 localStorage 为关闭状态
    try {
      const userInfoMsg = localStorage.getItem("userInfoMsg");
      if (userInfoMsg) {
        const userInfo = JSON.parse(userInfoMsg);
        userInfo.alphaMusic = 0;
        localStorage.setItem("userInfoMsg", JSON.stringify(userInfo));
      }
    } catch (e) {
      console.error('保存音乐状态失败:', e);
    }
  }

  // 销毁音频资源
  destroy() {
    if (this.audio) {
      this.pause();
      this.audio = null;
    }
  }
}

// 创建单例
const musicManager = new MusicManager();

export default musicManager;

